import { useMutation } from "@apollo/react-hooks";

import { Instructor, Learner, Lesson, UpdateLessonInput } from "@hubcapp/types";
import learners from "@hubcapp/graphql/learners.graphql";
import updateNotesMutation from "../graph-ql/updateNotes.graphql";

type UpdateLessonNotesData = {
  __typename: "Mutation";
  updateLesson: Pick<Lesson, "id" | "notes">;
};

const useMutationUpdateLesson = (instructorId: string, learnerId: string) =>
  useMutation<UpdateLessonNotesData, UpdateLessonInput>(updateNotesMutation, {
    optimisticResponse: ({ id, notes }) => ({
      __typename: "Mutation",
      updateLesson: {
        id,
        notes,
      },
    }),
    update: (proxy, { data }) => {
      if (data) {
        const learnerQuery = proxy.readQuery<{ instructor: Instructor }>({
          query: learners,
          variables: { id: instructorId },
        });

        if (learnerQuery) {
          const learner = learnerQuery.instructor.learners.find(
            learner => learner?.id === learnerId,
          );

          if (!learner) {
            return;
          }

          const learnerIndex = learnerQuery.instructor.learners.indexOf(
            learner,
          );

          const lessonIndex = learner.lessons
            .map(({ id }) => id)
            .indexOf(data.updateLesson.id);

          if (lessonIndex < 0) {
            return;
          }

          const filteredLessons = learner.lessons.filter(
            lesson => lesson.id !== data.updateLesson.id,
          );

          filteredLessons.splice(lessonIndex, 0, {
            ...learner.lessons[lessonIndex],
            notes: data.updateLesson.notes,
          });

          const filteredLearners = learnerQuery.instructor.learners.filter(
            learn => learn?.id !== learner.id,
          );

          filteredLearners.splice(learnerIndex, 0, {
            ...learnerQuery.instructor.learners[learnerIndex],
            lessons: [
              ...filteredLessons,
              (data.updateLesson as unknown) as Lesson,
            ],
          } as Learner);

          proxy.writeQuery({
            query: learners,
            variables: { id: learnerId },
            data: {
              instructor: {
                learners: filteredLearners,
              },
            },
          });
        }
      }
    },
  });

export default useMutationUpdateLesson;
